import {Tr} from "@/i18n/i18n";
import {injectable} from "inversify";
import ConsumablePanel from "@/scenes.ui/consumable-panel";
import {Consumable, ConsumableType} from "@/store/consumable/types";
import CreaturePanel from "@/scenes.ui/creature-panel";
import {Creature} from "@/store/level/types";
import {CreatureUtils} from "@/utils/creature-utils";
import {BattleResultPanel} from "@/scenes.ui/battle-result-panel";
import {BattleStateType, BattleStatus, BattleType, PenaltyDto, RewardDto} from "@/store/battle/types";
import {EffectType} from "@/store/artifact/types";
import {FeedbackPanel} from "@/scenes.ui/feedback-panel";
import {Settings} from "@/constants/settings";
import {BattleGiveUpButton} from "@/scenes.ui/battle-give-up-button";
import {BattleGiveUpConfirmationPanel} from "@/scenes.ui/battle-give-up-confirmation-panel";
import SoundUtils from "@/utils/sound-utils";
import BattleStore from "@/store/battle";
import PlayerStore from "@/store/player";
import {ImageButton} from "@/scenes.ui/image-button";
import CU from "@/utils/c-u";
import {Keys} from "@/constants/key";
import TextUtils from "@/utils/text-utils";
import {Fonts} from "@/constants/font";
import Scene = Phaser.Scene;
import Image = Phaser.GameObjects.Image;
import Container = Phaser.GameObjects.Container;

@injectable()
export class BattleService {

    private consumablePanel!: ConsumablePanel | null;
    private creaturePanel: CreaturePanel | null = null;
    private creaturePanelById!: Map<number, CreaturePanel>;
    private scene!: Scene;
    private resultPanel!: BattleResultPanel | null;
    private battleGiveUpButton!: BattleGiveUpButton | null;
    private skipButton!: ImageButton | null;
    private battleGiveUpConfirmationPanel!: BattleGiveUpConfirmationPanel | null;

    public createConsumablePanel(scene: Scene, canvas: HTMLCanvasElement, bs: BattleStatus, creaturePanelById: Map<number, CreaturePanel>,
                                 useConsumableCallback: (bs: BattleStatus) => unknown) {
        if (bs.consumables && bs.consumables.length > 0) {
            this.scene = scene;
            this.consumablePanel = new ConsumablePanel(scene, canvas, bs.consumables,
                (consumable: Consumable, container: Container, effectSound: string) => {
                    this.useConsumable(consumable, container, effectSound, useConsumableCallback);
                },
                (consumable: Consumable) => this.showCardBordersOnUse(consumable),
                () => this.hideCardBordersOffUse());
            scene.add.existing(this.consumablePanel);
            this.creaturePanelById = creaturePanelById;
        }
    }

    public setCurrentPanel(creaturePanel: CreaturePanel | null) {
        this.creaturePanel = creaturePanel;
    }

    private useConsumable(consumable: Consumable, container: Container | null, effectSound: string, useConsumableCallback: (bs: BattleStatus) => unknown) {

        if (this.creaturePanel && this.creaturePanelById) {

            if (this.onPlayerCreatureUsePossibility(consumable, this.creaturePanel)) {
                SoundUtils.addAndPlay(this.scene, effectSound, {volume: Settings.MUSIC_VOLUME});
                BattleStore.useConsumable(consumable.id, this.creaturePanel.getCreature().id).then((bs: BattleStatus) => {
                    container!.destroy();
                    useConsumableCallback(bs);
                });
            }

            if (this.onEnemyCreatureUsePossibility(consumable.consumableType, this.creaturePanel)){
                SoundUtils.addAndPlay(this.scene, effectSound, {volume: Settings.MUSIC_VOLUME});
                BattleStore.useConsumable(consumable.id, this.creaturePanel.getCreature().id).then((bs: BattleStatus) => {
                    container!.destroy();
                    useConsumableCallback(bs);
                });
            }
            this.creaturePanel = null;
        }

    }

    public disableConsumablePanelInteractions(){
        if (this.consumablePanel) { this.consumablePanel.disableConsumableInteractions(); }
    }

    public enableConsumablePanelInteractions(){
        if (this.consumablePanel) { this.consumablePanel.enableConsumableInteractions(); }
    }

    public showCardBordersOnUse(consumable: Consumable){
        if (this.creaturePanelById) {
            if (consumable.consumableType == ConsumableType.ENEMY) {
                BattleStore.getEnemyAliveCreatures().forEach((c: Creature) => { this.creaturePanelById.get(c.id)!.turnOnDefenderLook(); })
            }
            if (consumable.consumableType == ConsumableType.PLAYER) {
                BattleStore.getMyAliveCreatures().forEach((c: Creature) => { this.creaturePanelById.get(c.id)!.turnOnAttackerLook(); })
            }

        }
    }

    public hideCardBordersOffUse() {
        if (this.creaturePanelById) {
            BattleStore.getEnemyAliveCreatures().forEach((c: Creature) => { this.creaturePanelById.get(c.id)!.turnOnDefaultLook(); })
            BattleStore.getMyAliveCreatures().forEach((c: Creature) => { this.creaturePanelById.get(c.id)!.turnOnDefaultLook(); })
        }
    }

    public setupSceneDraggableActions(scene: Scene) {

        let self = this;

        scene.input.on('dragenter', function (pointer: any, dragObject: any, dropZone: CreaturePanel) {
            if (dragObject instanceof Image) { self.setCurrentPanel(dropZone); }
        })

        scene.input.on('dragleave', function (pointer: any, dragObject: any, dropZone: CreaturePanel) {
            if (dragObject instanceof Image) { self.setCurrentPanel(null); }
        })

    };

    public showBattleResultPanel(scene: Scene, canvas: HTMLCanvasElement, onAcceptCallback: (hasWon: boolean) => unknown,
                                 mainThemeSound: Phaser.Sound.BaseSound | undefined, victorySound: Phaser.Sound.BaseSound | undefined, defeatSound: Phaser.Sound.BaseSound | undefined) {

        let bs = BattleStore.getBattleStatus();
        if (bs && bs.battleState == BattleStateType.COMPLETED) {
            if (bs.battleType === BattleType.MULTI) {
                if (bs.winnerReward) {
                    this.createBattleResultPanel(scene, canvas, onAcceptCallback, mainThemeSound, victorySound, true, bs.winnerReward, undefined);
                } else {
                    let penalty: PenaltyDto = bs.looserPenalty;
                    this.createBattleResultPanel(scene, canvas, onAcceptCallback, mainThemeSound, defeatSound, false, undefined, penalty);
                }
            } else {
                if (BattleStore.getEnemyAliveCreatures().length == 0) {
                    let rewards = bs.winnerReward;
                    this.createBattleResultPanel(scene, canvas, onAcceptCallback, mainThemeSound, victorySound, true, rewards, undefined);
                } else {
                    let penalty: PenaltyDto = bs.looserPenalty;
                    this.createBattleResultPanel(scene, canvas, onAcceptCallback, mainThemeSound, defeatSound, false, undefined, penalty);
                }
            }
        }
    }

    public createBattleGiveUpButton(scene: Scene, canvas: HTMLCanvasElement, onAcceptUpCallback: (hasWon: boolean) => unknown,
                                          mainThemeSound: Phaser.Sound.BaseSound | undefined, victorySound: Phaser.Sound.BaseSound | undefined, defeatSound: Phaser.Sound.BaseSound | undefined,
                                    clickSound: Phaser.Sound.BaseSound | undefined) {

        if (PlayerStore.getPlayer() && PlayerStore.getPlayer()!.stair > 1) {
            this.battleGiveUpButton = new BattleGiveUpButton(scene, canvas,
                () => this.createBattleGiveUpConfirmationPanel(scene, canvas, onAcceptUpCallback, mainThemeSound, victorySound, defeatSound, clickSound), clickSound);
            scene.add.existing(this.battleGiveUpButton);
        }
    }

    public createSkipButton(scene: Scene, canvas: HTMLCanvasElement, callback: () => unknown) {
        if (PlayerStore.getPlayer().level < 3) {
            return;
        }
        this.skipButton = new ImageButton(scene!, 50, canvas.height / CU.S - 44, undefined, Keys.SKIP_MV).setVisible(true);
        let skipButtonText = TextUtils.addTextByFamily(scene, Tr('skip.mv'),
            0, 34, '#f6f6f6', 8, 'normal', 130, Fonts.MYRIADPRO_BOLD);
        this.skipButton.add(skipButtonText);
        scene.add.existing(this.skipButton);

        this.skipButton.setCallback(() => {
            callback();
        });
    }

    private createBattleGiveUpConfirmationPanel(scene: Scene, canvas: HTMLCanvasElement, onAcceptCallback: (hasWon: boolean) => unknown,
                                                mainThemeSound: Phaser.Sound.BaseSound | undefined, victorySound: Phaser.Sound.BaseSound | undefined, defeatSound: Phaser.Sound.BaseSound | undefined,
                                                clickSound: Phaser.Sound.BaseSound | undefined) {

        // Если бой УЖЕ закончен, то не показываем панель
        if (BattleStore.getBattleStatus()!.battleState == "COMPLETED") {
            return;
        }

        if (this.battleGiveUpConfirmationPanel) { this.battleGiveUpConfirmationPanel.destroy(); }

        this.battleGiveUpConfirmationPanel = new BattleGiveUpConfirmationPanel(scene, canvas,
            () => this.giveUpBattle(scene, canvas, onAcceptCallback, mainThemeSound, victorySound, defeatSound), clickSound);
        this.battleGiveUpConfirmationPanel.open();
    }

    private async giveUpBattle(scene: Scene, canvas: HTMLCanvasElement, onAcceptCallback: (hasWon: boolean) => unknown,
                               mainThemeSound: Phaser.Sound.BaseSound | undefined, victorySound: Phaser.Sound.BaseSound | undefined, defeatSound: Phaser.Sound.BaseSound | undefined) {
        await BattleStore.giveUpBattle();
        this.showBattleResultPanel(scene, canvas, onAcceptCallback, mainThemeSound, victorySound, defeatSound);
    }

    private createBattleResultPanel(scene: Scene, canvas: HTMLCanvasElement, onAcceptCallback: (hasWon: boolean) => unknown,
                                    mainThemeSound: Phaser.Sound.BaseSound | undefined, resultSound: Phaser.Sound.BaseSound | undefined, hasWon: boolean,
                                    rewards?: RewardDto, penalty?: PenaltyDto) {

        if (this.resultPanel) {
            this.resultPanel.destroy()
            this.resultPanel = null;
        }

        if (this.battleGiveUpButton) { this.battleGiveUpButton.destroy() }

        if (this.skipButton) { this.skipButton.destroy() }

        this.resultPanel = new BattleResultPanel(scene, canvas, hasWon, onAcceptCallback, rewards, penalty);
        scene.add.existing(this.resultPanel);

        SoundUtils.stop(mainThemeSound);
        SoundUtils.play(resultSound);
    }

    private onPlayerCreatureUsePossibility(consumable: Consumable, creaturePanel: CreaturePanel) {
        let creature: Creature = BattleStore.getMyCreatures().find((c: Creature) => c.id == creaturePanel.getCreature().id)!;

        // Если создание игрока и предмет типа элексир
        if (creature && consumable.consumableType == ConsumableType.PLAYER) {
            // Если этот элексир на хп и у игрока фулл хп - нельзя юзать
            if (consumable.effectType == EffectType.HEALTH && this.checkPlayerHealthFullness(creature)) {
                new FeedbackPanel(this.scene, Tr('personazh.polnostju.zdorov'), undefined, true).show();
                return false;
            }
            return true;
        }

    }

    private onEnemyCreatureUsePossibility(consType: ConsumableType, creaturePanel: CreaturePanel): boolean {
        let creature: Creature = BattleStore.getEnemyCreatures().find((c: Creature) => c.id == creaturePanel.getCreature().id)!;
        return !!(consType == ConsumableType.ENEMY && creature);
    }

    private checkPlayerHealthFullness(creature: Creature): boolean {
        return CreatureUtils.getUnitHealthCurrentValue(creature) >= CreatureUtils.getUnitHealthMax(creature);
    }

}
